import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react'
import { Box, Button, TextField, useMediaQuery, useTheme } from '@mui/material'
import { DateRange, DayPicker } from 'react-day-picker'
import moment from 'moment'

import { HelperWrapper } from '../helper-wrapper'
import { IFilter } from '../filter/types/filter.types'
import { SPACES } from 'src/constants'

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp'
import 'react-day-picker/style.css'
import { useStyles } from './styles'

interface IProps {
  clearFilter: () => void
  filter: IFilter
  setFilterDate: Dispatch<SetStateAction<IFilter>>
}

export default function FilterDateRangePicker({ clearFilter, filter, setFilterDate }: IProps) {
  const theme = useTheme()
  const [selected, setSelected] = useState<DateRange>()
  const [open, setOpen] = useState<boolean>(false)

  const refOne = useRef<HTMLDivElement | null>(null)

  const classes = useStyles()

  const isLaptop = useMediaQuery(theme.breakpoints.up('laptop_l'))

  useEffect(() => {
    const hideOnEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false)
      }
    }

    const hideOnClickOutside = (e: MouseEvent) => {
      if (refOne.current && !refOne.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }

    if (open) {
      document.addEventListener('keydown', hideOnEscape, true)
      document.addEventListener('click', hideOnClickOutside, true)
    }

    return () => {
      document.removeEventListener('keydown', hideOnEscape, true)
      document.removeEventListener('click', hideOnClickOutside, true)
    }
  }, [open])

  const handleClear = () => {
    setSelected(undefined)
    clearFilter()
  }
  const handleSelect = (e: DateRange | undefined) => {
    setSelected(e)
    if (e && e.from && e.to) {
      const dateRangeFrom = moment(e.from).format('YYYY-MM-DDTHH:mm:ssZ') // ISO 8601 format
      const dateRangeTo = moment(e.to).format('YYYY-MM-DDTHH:mm:ssZ') // ISO 8601 format
      setFilterDate({ ...filter, date_range_from: dateRangeFrom, date_range_to: dateRangeTo })
    } else {
      clearFilter()
    }
  }

  return (
    <Box
      sx={{
        position: 'relative',
        '.rdp-root': {
          backgroundColor: theme.palette.custom.white,
          position: 'absolute',
          top: isLaptop ? 40 : 35,
          left: 0,
          zIndex: 100,
          border: `1px solid ${theme.palette.custom.grey}`,
          borderRadius: '4px',
          boxShadow:
            '0px 5px 5px -3px rgba(0, 0, 0, 0.2), 0px 8px 10px 1px rgba(0, 0, 0, 0.14), 0px 3px 14px 2px rgba(0, 0, 0, 0.12)',
          padding: '8px 12px',
          '--rdp-accent-color': theme.palette.custom.primary,
          '--rdp-today-color': theme.palette.custom.primary,
          '--rdp-range_middle-background-color': theme.palette.custom.grey,
          '--rdp-day-height': '36px',
          '--rdp-day-width': '36px',

          '.rdp-month_caption': {
            fontSize: '16px',
            fontWeight: 700,
            '.rdp-chevron': { marginLeft: '8px' },
            '.rdp-dropdown': {
              width: '100%',

              paddingLeft: '5px',
              paddingRight: '5px',
            },
          },
        },
        '.rdp-root button': {
          fontSize: '14px',
        },
      }}
    >
      <Box ref={refOne} sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <TextField
          id="filter-date-range-picker"
          onClick={(e) => {
            e.stopPropagation()
            setOpen((prev) => !prev)
          }}
          sx={{ '.MuiInputBase-input.MuiOutlinedInput-input': { cursor: 'pointer' } }}
          value={
            selected
              ? `${selected.from?.toLocaleDateString() || 'from'} - ${selected.to?.toLocaleDateString() || 'to'}`
              : 'ALL'
          }
          InputProps={{
            readOnly: true,
            style: {
              width: '240px',
              border: `1px solid ${theme.palette.custom.grey}`,
              borderRadius: '30px',
              paddingLeft: SPACES.xxl,
              paddingRight: SPACES.xl,
              fontSize: '12px',
              fontWeight: 700,
              height: isLaptop ? 40 : 34,
              cursor: 'pointer',
            },
            startAdornment: (
              <span className={classes.startAdornment}>
                <HelperWrapper t="date-range-picker-label" />:
              </span>
            ),
            endAdornment: open ? (
              <ArrowDropUpIcon sx={{ color: 'rgba(0, 0, 0, 0.54)' }} />
            ) : (
              <ArrowDropDownIcon sx={{ color: 'rgba(0, 0, 0, 0.54)' }} />
            ),
          }}
        />

        <Button
          onClick={handleClear}
          sx={{ fontSize: '10px', color: theme.palette.custom.grey2, gap: '6px' }}
        >
          <HelperWrapper t="clear-filter-button-lable" />
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            stroke-width="2"
            stroke={theme.palette.custom.grey2}
            fill="none"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
            <path d="M20 11a8.1 8.1 0 0 0 -15.5 -2m-.5 -4v4h4"></path>
            <path d="M4 13a8.1 8.1 0 0 0 15.5 2m.5 4v-4h-4"></path>
          </svg>
        </Button>

        {open && (
          <DayPicker
            mode="range"
            selected={selected}
            onSelect={handleSelect}
            numberOfMonths={1}
            captionLayout="dropdown"
          />
        )}
      </Box>
    </Box>
  )
}
